import { Quat, Vec3 } from 'playcanvas';

import { CubicSpline } from './anim/spline';
import { AnimTrack } from './anim-track';
import { Events } from './events';

type Pose = {
    name: string,
    frame: number,
    position: Vec3,
    target: Vec3,
    fov?: number,
    id?: string,
    thumbnailUrl?: string,
    imageUrl?: string,
    label?: string,
    group?: string
};

type CameraPoseTransform = {
    position: [number, number, number],
    rotation: [number, number, number],
    scale: number
};

const defaultCameraPoseTransform: CameraPoseTransform = {
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    scale: 1
};

const defaultPoseGizmoScale = 1;

/**
 * Camera animation track that manages camera keyframes and interpolation.
 * Implements AnimTrack interface so it can be used with the timeline system.
 *
 * Fully self-contained: subscribes to timeline events internally for
 * evaluation and spline rebuilding.
 */
class CameraAnimTrack implements AnimTrack {
    private poses: Pose[] = [];
    private events: Events;
    private onTimelineChange: ((frame: number) => void) | null = null;

    constructor(events: Events) {
        this.events = events;

        // Evaluate on timeline playback and scrub
        events.on('timeline.time', (time: number) => {
            this.evaluate(time);
        });

        events.on('timeline.frame', (frame: number) => {
            this.evaluate(frame);
        });

        // Rebuild spline when timeline parameters change
        events.on('timeline.frames', () => {
            this.rebuildSpline();
        });

        events.on('timeline.smoothness', () => {
            this.rebuildSpline();
        });

        events.on('timeline.loop', () => {
            this.rebuildSpline();
        });

        // Clear track when scene is cleared
        events.on('scene.clear', () => {
            this.clear();
        });
    }

    get keys(): readonly number[] {
        return this.poses.map(p => p.frame);
    }

    addKey(frame: number): boolean {
        const pose = this.events.invoke('camera.getPose');
        if (!pose) return false;

        const existingIndex = this.poses.findIndex(p => p.frame === frame);

        const newPose: Pose = {
            name: `camera_${this.poses.length}`,
            frame,
            position: new Vec3(pose.position.x, pose.position.y, pose.position.z),
            target: new Vec3(pose.target.x, pose.target.y, pose.target.z),
            fov: pose.fov
        };

        if (existingIndex === -1) {
            this.poses.push(newPose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', frame);
        } else {
            this.poses[existingIndex] = newPose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', frame);
        }
        return true;
    }

    removeKey(frame: number): boolean {
        const index = this.poses.findIndex(p => p.frame === frame);
        if (index === -1) return false;
        this.poses.splice(index, 1);
        this.rebuildSpline();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const index = this.poses.findIndex(p => p.frame === fromFrame);
        if (index === -1) return false;

        // Remove any existing pose at the target frame
        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        // Update the frame (re-find index since splice may have shifted it)
        const movedIndex = this.poses.findIndex(p => p.frame === fromFrame);
        this.poses[movedIndex].frame = toFrame;
        this.rebuildSpline();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;

        const source = this.poses.find(p => p.frame === fromFrame);
        if (!source) return false;

        // Remove any existing pose at the target frame
        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        this.poses.push({
            name: `camera_${this.poses.length}`,
            frame: toFrame,
            position: source.position.clone(),
            target: source.target.clone(),
            fov: source.fov
        });

        this.rebuildSpline();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    evaluate(frame: number): void {
        this.onTimelineChange?.(frame);
    }

    clear(): void {
        this.poses.length = 0;
        this.onTimelineChange = null;
        // Notify after the initiating UI event has finished dispatching. This
        // avoids a nested PCUI destroy/observer-unregister race while the
        // Clear Cameras button is handling its click.
        queueMicrotask(() => this.events.fire('track.keysCleared'));
    }

    snapshot(): Pose[] {
        return this.poses.map(p => ({
            ...p,
            position: p.position.clone(),
            target: p.target.clone(),
            fov: p.fov
        }));
    }

    restore(snapshot: unknown): void {
        this.poses = (snapshot as Pose[]).map(p => ({
            ...p,
            position: p.position.clone(),
            target: p.target.clone(),
            fov: p.fov
        }));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    /**
     * Add a pose directly (used for deserialization).
     */
    addPose(pose: Pose): void {
        if (pose.frame === undefined) {
            return;
        }

        pose.fov ??= this.events.invoke('camera.fov') ?? 60;

        const idx = this.poses.findIndex(p => p.frame === pose.frame);
        if (idx !== -1) {
            this.poses[idx] = pose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', pose.frame);
        } else {
            this.poses.push(pose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', pose.frame);
        }
    }

    /** Add imported poses in one update so the UI is not rebuilt per pose. */
    addPoses(poses: Pose[]): void {
        poses.forEach((pose) => {
            if (pose.frame === undefined) return;
            pose.fov ??= this.events.invoke('camera.fov') ?? 60;

            const idx = this.poses.findIndex(p => p.frame === pose.frame);
            if (idx !== -1) {
                this.poses[idx] = pose;
            } else {
                this.poses.push(pose);
            }
        });

        if (poses.length > 0) {
            this.rebuildSpline();
            this.events.fire('track.keysLoaded');
        }
    }

    /**
     * Get all poses (used for serialization).
     */
    getPoses(): readonly Pose[] {
        return this.poses;
    }

    /**
     * Load poses from serialized data.
     */
    loadPoses(posesData: Pose[]): void {
        const defaultFov = this.events.invoke('camera.fov');
        this.poses.length = 0;
        posesData.forEach((pose) => {
            this.poses.push({
                ...pose,
                fov: pose.fov ?? defaultFov
            });
        });
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    private rebuildSpline(): void {
        const duration = this.events.invoke('timeline.frames');
        const smoothness = this.events.invoke('timeline.smoothness');
        const loop = this.events.invoke('timeline.loop');

        const orderedPoses = this.poses.slice()
        .filter(a => a.frame < duration)
        .sort((a, b) => a.frame - b.frame);

        const times = orderedPoses.map(p => p.frame);
        const points: number[] = [];
        for (let i = 0; i < orderedPoses.length; ++i) {
            const p = orderedPoses[i];
            points.push(p.position.x, p.position.y, p.position.z);
            points.push(p.target.x, p.target.y, p.target.z);
            points.push(p.fov);
        }

        if (orderedPoses.length > 1) {
            // when not looping the spline clamps, holding the first/last pose
            // beyond the outer keys instead of wrapping the end into the start
            const spline = loop ?
                CubicSpline.fromPointsLooping(duration, times, points, smoothness) :
                CubicSpline.fromPoints(times, points, smoothness);
            const result: number[] = [];
            const pose = { position: new Vec3(), target: new Vec3(), fov: 0 };

            this.onTimelineChange = (frame: number) => {
                spline.evaluate(frame, result);
                pose.position.set(result[0], result[1], result[2]);
                pose.target.set(result[3], result[4], result[5]);
                pose.fov = result[6];
                this.events.fire('camera.setPose', pose, 0);
            };
        } else if (orderedPoses.length === 1) {
            // a single key can't form a spline; hold its pose at every frame
            const p = orderedPoses[0];
            const pose = { position: p.position.clone(), target: p.target.clone(), fov: p.fov };

            this.onTimelineChange = () => {
                this.events.fire('camera.setPose', pose, 0);
            };
        } else {
            this.onTimelineChange = null;
        }

        // re-evaluate at the current frame so the camera updates immediately
        this.evaluate(this.events.invoke('timeline.frame'));
    }
}

/**
 * Register the camera animation track and expose it via events.
 * The track is fully self-contained (subscribes to timeline events internally),
 * so this function only needs to create it, expose it, and handle serialization.
 */
const registerCameraPosesEvents = (events: Events) => {
    const track = new CameraAnimTrack(events);
    let previewPoses: Pose[] | null = null;
    let poseTransform = { ...defaultCameraPoseTransform };
    let poseGizmoScale = defaultPoseGizmoScale;

    events.function('camera.poseTransform', () => ({
        position: [...poseTransform.position] as [number, number, number],
        rotation: [...poseTransform.rotation] as [number, number, number],
        scale: poseTransform.scale
    }));

    events.function('camera.transformPose', (pose: { position: Vec3, target: Vec3 }) => {
        const transform = poseTransform;
        const q = new Quat().setFromEulerAngles(transform.rotation[0], transform.rotation[1], transform.rotation[2]);
        const translation = new Vec3(transform.position);
        const position = pose.position.clone().mulScalar(transform.scale);
        const target = pose.target.clone().mulScalar(transform.scale);
        q.transformVector(position, position).add(translation);
        q.transformVector(target, target).add(translation);
        return { position, target };
    });

    events.on('camera.setPoseTransform', (transform: Partial<CameraPoseTransform>) => {
        poseTransform = {
            position: transform.position ?? poseTransform.position,
            rotation: transform.rotation ?? poseTransform.rotation,
            scale: transform.scale ?? poseTransform.scale
        };
        events.fire('camera.poseTransform', poseTransform);
    });

    events.function('camera.poseGizmoScale', () => poseGizmoScale);
    events.on('camera.setPoseGizmoScale', (scale: number) => {
        poseGizmoScale = Math.max(0.1, Math.min(10, scale));
        events.fire('camera.poseGizmoScale', poseGizmoScale);
    });

    const notifyPosesChanged = () => events.fire('camera.posesChanged', track.getPoses());
    events.on('track.keyAdded', notifyPosesChanged);
    events.on('track.keyRemoved', notifyPosesChanged);
    events.on('track.keysCleared', notifyPosesChanged);
    events.on('track.keysLoaded', notifyPosesChanged);

    // Expose the camera animation track
    events.function('camera.animTrack', () => {
        return track;
    });

    // expose poses
    events.function('camera.poses', () => {
        return previewPoses ?? track.getPoses();
    });

    // Temporarily replace the poses exposed to camera UI and gizmos. This is
    // used by export previews and is deliberately kept separate from the
    // animation track so it can be restored without changing the document.
    events.function('camera.setPosePreview', (poses: Pose[] | null) => {
        previewPoses = poses;
        events.fire('camera.posesChanged', previewPoses ?? track.getPoses());
    });

    events.on('camera.loadPoses', (poses: Pose[]) => {
        track.loadPoses(poses);
    });
    events.on('camera.addPoses', (poses: Pose[]) => {
        track.addPoses(poses);
    });
    events.on('camera.clearPoses', () => {
        track.clear();
    });

    // Serialization

    events.function('docSerialize.poseSets', (): any[] => {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const poses = track.getPoses();

        if (poses.length === 0) {
            return [];
        }

        return [{
            name: 'set0',
            poses: poses.map((pose) => {
                return {
                    ...pose,
                    name: pose.name,
                    frame: pose.frame,
                    position: pack3(pose.position),
                    target: pack3(pose.target),
                    fov: pose.fov,
                    id: pose.id,
                    thumbnailUrl: pose.thumbnailUrl,
                    imageUrl: pose.imageUrl,
                    label: pose.label,
                    group: pose.group
                };
            })
        }];
    });

    events.function('docDeserialize.poseSets', (poseSets: any[], documentCameraFov?: number) => {
        if (!poseSets || poseSets.length === 0) {
            return;
        }

        const fps = events.invoke('timeline.frameRate');

        const defaultFov = documentCameraFov ?? events.invoke('camera.fov') ?? 60;

        const loadedPoses: Pose[] = poseSets[0].poses.map((docPose: any, index: number) => {
            return {
                ...docPose,
                name: docPose.name,
                frame: docPose.frame ?? (index * fps),
                position: new Vec3(docPose.position),
                target: new Vec3(docPose.target),
                fov: docPose.fov ?? defaultFov
            };
        });

        track.loadPoses(loadedPoses);
    });
};

export { registerCameraPosesEvents, CameraAnimTrack, Pose };
